import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useParams } from 'react-router-dom';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  salePrice: number;
  image: string;
  unit: string;
  category: string;
  stock: number;
  createdAt: any;
}

export function useProducts(categoryId?: string) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { storeSlug } = useParams<{ storeSlug: string }>();

  useEffect(() => {
    async function loadProducts() {
      if (!storeSlug) {
        setError('Store slug not found');
        setLoading(false);
        return;
      }

      try {
        // First, find the store document by slug
        const storesRef = collection(db, 'stores');
        const storeQuery = query(storesRef, where('slug', '==', storeSlug));
        const storeSnapshot = await getDocs(storeQuery);

        if (storeSnapshot.empty) {
          setError('Store not found');
          setLoading(false);
          return;
        }

        const storeId = storeSnapshot.docs[0].id;

        // Then get the products for this store
        const productsRef = collection(db, 'products');
        let productsQuery = query(
          productsRef,
          where('storeId', '==', storeId),
          where('active', '==', true),
          orderBy('createdAt', 'desc')
        );

        if (categoryId) {
          productsQuery = query(
            productsRef,
            where('storeId', '==', storeId),
            where('categoryId', '==', categoryId),
            where('active', '==', true),
            orderBy('createdAt', 'desc')
          );
        }

        const querySnapshot = await getDocs(productsQuery);
        const productsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Product[];

        setProducts(productsData);
      } catch (err) {
        console.error('Error loading products:', err);
        setError('Error loading products');
      } finally {
        setLoading(false);
      }
    }

    loadProducts();
  }, [storeSlug, categoryId]);

  return { products, loading, error };
}
